"use client";

import { cn } from "@/lib/utils";
import type { Participant } from "../base-match-template";
import {
  CourtSvg,
  courtLines,
  courtQuad,
  courtStripes,
  createCourtProjector,
  getRacketMatchState,
  PAINT_LINE,
  PropShadow,
  RacketMatchFrame,
  RacketScore,
  useSvgIdPrefix,
} from "./racket-court-kit";

export type BadmintonCourtPreviewProps = {
  mode: "singles" | "doubles";
  yourTeam: Participant[];
  opponentTeam: Participant[];
  yourWon?: boolean;
  showLabels?: boolean;
  linkToProfiles?: boolean;
  matchScore?: { yourGames: number; opponentGames: number };
  games?: Array<{ yourScore: number; opponentScore: number }>;
};

// ─── Court geometry (BWF arena: green mat on a blue floor) ──────────────────
// 13.40m × 6.10m: singles sidelines 0.46m in, doubles long service lines
// 0.76m in from the baseline, short service lines 1.98m off the net. Posts sit
// on the doubles sidelines.

const P = createCourtProjector({
  yFar: 56,
  yNear: 192,
  farHalfWidth: 112,
  nearHalfWidth: 136,
});

const SINGLES = 0.075;
const LONG_SERVICE = 0.0567;
const SHORT_SERVICE = 0.352;

const MAT = courtQuad(P, -0.075, -0.16, 1.075, 1.14);
const MAT_SHEEN = courtStripes(P, 10, -0.075, 1.075, -0.16, 1.14);
const COURT = courtQuad(P, 0, 0, 1, 1);
const OUTER_LINES = courtLines(P, [
  [0, 0, 1, 0],
  [1, 0, 1, 1],
  [1, 1, 0, 1],
  [0, 1, 0, 0],
]);
const INNER_LINES = courtLines(P, [
  [0, SINGLES, 1, SINGLES],
  [0, 1 - SINGLES, 1, 1 - SINGLES],
  [LONG_SERVICE, 0, LONG_SERVICE, 1],
  [1 - LONG_SERVICE, 0, 1 - LONG_SERVICE, 1],
  [SHORT_SERVICE, 0, SHORT_SERVICE, 1],
  [1 - SHORT_SERVICE, 0, 1 - SHORT_SERVICE, 1],
  [0, 0.5, SHORT_SERVICE, 0.5],
  [1 - SHORT_SERVICE, 0.5, 1, 0.5],
]);
const NET_SHADOW = courtQuad(P, 0.5, 0, 0.52, 1);
const [, NET_FAR_Y] = P(0.5, 0);
const [, NET_NEAR_Y] = P(0.5, 1);

function BadmintonCourtArt() {
  const id = useSvgIdPrefix();
  return (
    <>
      <CourtSvg>
        <defs>
          <linearGradient id={`${id}-stands`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#020814" />
            <stop offset="1" stopColor="#071b33" />
          </linearGradient>
          <linearGradient id={`${id}-board`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#0d4f9a" />
            <stop offset="0.5" stopColor="#1f7fe0" />
            <stop offset="1" stopColor="#0d4f9a" />
          </linearGradient>
          <linearGradient id={`${id}-floor`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#0f4a86" />
            <stop offset="1" stopColor="#072a52" />
          </linearGradient>
          <linearGradient id={`${id}-mat`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2aa56d" />
            <stop offset="0.6" stopColor="#1a8556" />
            <stop offset="1" stopColor="#106640" />
          </linearGradient>
          <pattern
            id={`${id}-crowd`}
            width="7"
            height="5"
            patternUnits="userSpaceOnUse"
          >
            <rect x="1" y="1" width="2.2" height="2" rx="1" fill="#1b3456" />
            <rect x="4.5" y="3" width="2.2" height="2" rx="1" fill="#24426a" />
          </pattern>
          <pattern
            id={`${id}-mesh`}
            width="2"
            height="2"
            patternUnits="userSpaceOnUse"
          >
            <rect width="2" height="2" fill="rgba(8,16,24,0.6)" />
            <rect
              y="1.4"
              width="2"
              height="0.4"
              fill="rgba(255,255,255,0.35)"
            />
          </pattern>
        </defs>

        {/* Arena: crowd in the dark stands, a glowing LED board. */}
        <rect width="300" height="44" fill={`url(#${id}-stands)`} />
        <rect
          width="300"
          height="24"
          fill={`url(#${id}-crowd)`}
          opacity="0.7"
        />
        <rect y="26" width="300" height="12" fill={`url(#${id}-board)`} />
        {[22, 70, 190, 238].map((x) => (
          <rect
            key={x}
            x={x}
            y="30"
            width="40"
            height="4"
            rx="1"
            fill="rgba(235,248,255,0.55)"
          />
        ))}
        <rect y="38" width="300" height="1.5" fill="rgba(160,215,255,0.55)" />

        {/* Blue floor, green mat, the mat's satin sheen. */}
        <rect y="39.5" width="300" height="181" fill={`url(#${id}-floor)`} />
        <path d={MAT} fill={`url(#${id}-mat)`} />
        <path d={MAT_SHEEN} fill="rgba(255,255,255,0.035)" />
        <path d={COURT} fill="rgba(255,255,255,0.03)" />

        <path
          d={OUTER_LINES}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.95)"
          strokeWidth="1.8"
        />
        <path
          d={INNER_LINES}
          {...PAINT_LINE}
          stroke="rgba(255,255,255,0.86)"
          strokeWidth="1.4"
        />

        {/* Net: shadow, mesh, white tape, posts on the sidelines. */}
        <path d={NET_SHADOW} fill="rgba(2,30,18,0.25)" />
        <rect
          x="148.2"
          y={NET_FAR_Y - 9}
          width="3.6"
          height={NET_NEAR_Y - NET_FAR_Y - 9}
          fill={`url(#${id}-mesh)`}
        />
        <rect
          x="147.6"
          y={NET_FAR_Y - 9}
          width="1.4"
          height={NET_NEAR_Y - NET_FAR_Y - 9}
          fill="#f6f8fb"
        />
        <rect
          x="147.4"
          y={NET_FAR_Y - 11}
          width="5.2"
          height="12"
          rx="1"
          fill="#d8dee6"
        />
        <rect
          x="146.2"
          y={NET_NEAR_Y - 22}
          width="7.6"
          height="20"
          rx="1.4"
          fill="#e3e8ee"
        />
        <rect
          x="150.6"
          y={NET_NEAR_Y - 22}
          width="3.2"
          height="20"
          rx="1"
          fill="rgba(40,60,80,0.35)"
        />
        <rect
          x="143"
          y={NET_NEAR_Y - 3}
          width="14"
          height="6"
          rx="1.5"
          fill="#1b2a3c"
        />
      </CourtSvg>

      {/* Lighting: arena spots reflected in the satin mat. */}
      <div className="absolute inset-x-0 top-0 h-[60px] bg-[radial-gradient(ellipse_20%_70%_at_30%_0%,rgba(210,236,255,0.32),transparent_70%),radial-gradient(ellipse_20%_70%_at_70%_0%,rgba(210,236,255,0.32),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_16%_22%_at_31%_46%,rgba(225,255,240,0.2),transparent_70%),radial-gradient(ellipse_16%_22%_at_69%_46%,rgba(225,255,240,0.2),transparent_70%)]" />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_55%,rgba(2,12,28,0.4)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_90%_110%_at_50%_45%,transparent_55%,rgba(0,0,0,0.42)_100%)]" />
    </>
  );
}

const Shuttlecock = ({ className }: { className?: string }) => (
  <div className={cn("absolute h-[24px] w-[20px]", className)}>
    {/* Feather skirt */}
    <div
      className="absolute top-0 left-1/2 h-[17px] w-[20px] -translate-x-1/2 bg-[linear-gradient(180deg,#ffffff_0%,#e9edf2_70%,#cfd6de_100%)]"
      style={{ clipPath: "polygon(0 0, 100% 0, 72% 100%, 28% 100%)" }}
    >
      <div className="absolute inset-y-0 left-[30%] w-px bg-black/12" />
      <div className="absolute inset-y-0 left-1/2 w-px bg-black/12" />
      <div className="absolute inset-y-0 left-[70%] w-px bg-black/12" />
      <div className="absolute inset-x-0 top-[55%] h-[2px] bg-[#1f7fe0]/70" />
    </div>
    {/* Cork */}
    <div className="absolute bottom-0 left-1/2 h-[8px] w-[9px] -translate-x-1/2 rounded-t-[2px] rounded-b-full bg-[radial-gradient(circle_at_40%_30%,#fff8ec,#d9c7b0)] shadow-[inset_-1px_-2px_2px_rgba(0,0,0,0.25)]" />
  </div>
);

export function BadmintonCourtPreview({
  mode: _mode,
  yourTeam,
  opponentTeam,
  yourWon,
  linkToProfiles = false,
  matchScore,
  games,
}: BadmintonCourtPreviewProps) {
  const opponentWon = opponentTeam.length > 0 && yourWon === false;
  const yourIsWinner = !!yourWon && !opponentWon;

  const hasScore =
    !!matchScore &&
    (matchScore.yourGames > 0 ||
      matchScore.opponentGames > 0 ||
      (games?.length ?? 0) > 0);
  const matchState = getRacketMatchState(yourTeam, opponentTeam, hasScore);

  return (
    <RacketMatchFrame
      label="Badminton"
      frameClassName="bg-[#061a33]"
      matchState={matchState}
      scene={<BadmintonCourtArt />}
      decor={
        matchState === "FULL" ? (
          <>
            <PropShadow className="top-[85%] left-[31%] h-[6px] w-[24px]" />
            <Shuttlecock className="top-[77%] left-[32%] rotate-[100deg] drop-shadow-[0_2px_2px_rgba(0,0,0,0.4)]" />
          </>
        ) : (
          <>
            {/* Shuttle at the top of its arc over the right court. */}
            <PropShadow className="top-[66%] left-[60%] h-[6px] w-[22px]" />
            <Shuttlecock className="top-[34%] left-[61%] rotate-[-35deg] drop-shadow-[0_6px_6px_rgba(0,0,0,0.35)]" />
          </>
        )
      }
      yourTeam={yourTeam}
      opponentTeam={opponentTeam}
      yourWinner={yourIsWinner}
      opponentWinner={opponentWon}
      linkToProfiles={linkToProfiles}
      center={
        matchState === "FULL" && matchScore ? (
          <RacketScore
            left={matchScore.yourGames}
            right={matchScore.opponentGames}
            leftWinner={yourIsWinner}
            rightWinner={opponentWon}
            breakdown={games}
            accentClassName="bg-[#7fd0ff] shadow-[0_0_12px_rgba(127,208,255,0.7)]"
          />
        ) : null
      }
    />
  );
}
